import { useState, useCallback } from 'react'
import { useQuery } from 'react-query'
import getLocation from 'services/api/time-attendance'

const useLocationDetection = ({ onError } = {}) => {
  const [permission, setPermission] = useState(null)
  const [coordinates, setCoordinates] = useState(null)
  const [locationLoading, setLocationLoading] = useState(false)

  const validateAccuracy = (accuracy) => {
    if (!accuracy) return { level: 'unknown' }
    if (accuracy <= 20) return { level: 'excellent' }
    if (accuracy <= 50) return { level: 'good' }
    if (accuracy <= 100) return { level: 'fair' }
    return { level: 'poor' }
  }

  const requestLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error('Geolocation not supported'))
      const opts = { enableHighAccuracy: true, maximumAge: 0 }
      let done = false
      const finish = (fn, val) => { if (done) return; done = true; fn(val) }
      const timer = setTimeout(() => {
        try { navigator.geolocation.clearWatch(watchId) } catch {}
        finish(reject, Object.assign(new Error('Timeout'), { code: 3 }))
      }, 15000)
      const watchId = navigator.geolocation.watchPosition(
        (pos) => { clearTimeout(timer); try { navigator.geolocation.clearWatch(watchId) } catch {}; finish(resolve, pos) },
        (err) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => { clearTimeout(timer); finish(resolve, pos) },
            (e) => { clearTimeout(timer); finish(reject, e || err) },
            { ...opts, timeout: 15000 }
          )
        },
        opts
      )
    })

  const getUserLocation = useCallback(async () => {
    if (locationLoading) return
    if (!navigator.geolocation) {
      onError?.('Location services are not supported by your browser.')
      setPermission('denied')
      return
    }
    setLocationLoading(true)
    try {
      const position = await requestLocation()
      const { latitude, longitude, accuracy } = position.coords
      setCoordinates({ latitude, longitude, accuracy, validation: validateAccuracy(accuracy), timestamp: Date.now() })
      setPermission('granted')
    } catch (error) {
      setCoordinates(null)
      if (error?.code === 1) {
        setPermission('denied')
        onError?.('Location access is required. Please allow location access.')
      } else {
        setPermission(null)
        onError?.('Unable to get your location. Please try again.')
      }
    } finally {
      setLocationLoading(false)
    }
  }, [locationLoading, onError])

  const hasLatLon = coordinates?.latitude != null && coordinates?.longitude != null

  const { data: locationData, isLoading: geocodingLoading, isError: geocodingError } = useQuery(
    ['getLocation', coordinates?.latitude, coordinates?.longitude],
    async () => await getLocation(coordinates?.latitude, coordinates?.longitude),
    { refetchOnWindowFocus: false, enabled: hasLatLon }
  )

  return { permission, coordinates, locationLoading, locationData, geocodingLoading, geocodingError, getUserLocation }
}

export default useLocationDetection